import { useIsAuthenticated } from '@azure/msal-react';
import { useCustomDomainUserInformation } from '@Contexts/customDomain/useCustomDomainUserInformation';
import { useSession } from '@Contexts/session/SessionContext';
import { type ApiUserPartnershipInformation, EnumAccessState, EnumPartnershipConsent } from '@Model';
import { LoadingFrame } from '@synqo/frontend-component-library';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { AcceptPartnership } from './AcceptPartnership';
import { LoginPage } from './LoginPage';
import { routingReservedKeywords } from '../../Routing/moduleRoots';

interface ILoginGuardProps {
    loginPageRoute: string;
}

// A pending partnership is selectable so the user can be asked to accept it; only a locked one is
// unreachable. Every caller below therefore matches on "not locked" and the pending case is handled
// by rendering the acceptance prompt instead of the app.
function isSelectable(partnership: ApiUserPartnershipInformation): boolean {
    return partnership.access_state !== EnumAccessState.Locked;
}

function findUniqueActivePartnership(
    partnerships: ApiUserPartnershipInformation[] | undefined,
    predicate: (partnership: ApiUserPartnershipInformation) => boolean,
): ApiUserPartnershipInformation | undefined {
    if (!partnerships) {
        return undefined;
    }

    const matches = partnerships.filter((p) => isSelectable(p) && predicate(p));

    return matches[0];
}

function stripFirstSegment(pathname: string): string {
    const remainder = pathname.split('/').slice(2).join('/');
    return remainder.length > 0 ? `/${remainder}` : '/';
}

export function LoginGuard(props: PropsWithChildren<ILoginGuardProps>) {
    const isAuthenticated = useIsAuthenticated();
    const { isLoading, data: userInformation, refetch: refetchUserInformation } = useCustomDomainUserInformation();
    const { current, update, setPartnerships } = useSession();
    const location = useLocation();
    const { subpath: urlSubpath } = useParams();

    const allPartnerships = userInformation?.partnerships;
    const { partnershipId: currentPartnershipId } = current;

    useEffect(() => {
        setPartnerships(allPartnerships ?? []);
    }, [allPartnerships, setPartnerships]);

    const partnershipFromUrl = useMemo(() => {
        if (!urlSubpath) {
            return undefined;
        }
        const matches = (allPartnerships ?? []).filter((p) => isSelectable(p) && p.mandant_shorthand === urlSubpath);
        if (matches.length <= 1) {
            return matches[0];
        }
        return matches.find((p) => p.partnership_id === currentPartnershipId);
    }, [allPartnerships, urlSubpath, currentPartnershipId]);

    const partnershipFromSession = useMemo(
        () =>
            currentPartnershipId
                ? findUniqueActivePartnership(allPartnerships, (p) => p.partnership_id === currentPartnershipId)
                : undefined,
        [allPartnerships, currentPartnershipId],
    );

    const isBadSubpath = useMemo(
        () => !!urlSubpath && !partnershipFromUrl && !routingReservedKeywords.includes(urlSubpath),
        [urlSubpath, partnershipFromUrl],
    );

    const activePartnership = useMemo(
        () => partnershipFromUrl ?? (isBadSubpath ? undefined : partnershipFromSession),
        [partnershipFromUrl, isBadSubpath, partnershipFromSession],
    );

    useEffect(() => {
        if (!activePartnership) {
            return;
        }

        update({
            partnershipId: activePartnership.partnership_id,
            partnershipType: activePartnership.type,
            clientId: activePartnership.client_id,
            subpath: activePartnership.mandant_shorthand,
            mandantId: activePartnership.mandant_id,
        });
    }, [activePartnership, update]);

    if (isLoading) {
        return <LoadingFrame />;
    }

    if (isBadSubpath) {
        return <LoginPage redirectAfterSelectionPath={stripFirstSegment(location.pathname)} />;
    }

    if (!isAuthenticated || !activePartnership) {
        return <LoginPage />;
    }

    // The user was created in this partnership but never agreed to it, so nothing behind the guard may
    // render until they decide. Checked before the redirect below so the prompt is reachable from any URL.
    if (activePartnership.partnership_consent === EnumPartnershipConsent.Undecided) {
        return <AcceptPartnership partnership={activePartnership} refetchUserInformation={refetchUserInformation} />;
    }

    if (!partnershipFromUrl) {
        return <Navigate to={`/${activePartnership.mandant_shorthand}${location.pathname}`} replace />;
    }

    return props.children;
}
